import type { AutopopStepKey } from '@krit/core';
import { extractFromDeck, registryFetch, industryFetch, peersPopulate, researchFetch, briefingFetch, teamResearchFetch, questionsFetch, scoreFetch, moduleRatingsFetch } from './api.js';

/**
 * Client-sequenced auto-populate: the browser runs each enrichment pass as its OWN foreground
 * request, awaiting each in turn. Foreground requests are not subject to Cloudflare's
 * background-invocation cap, so every pass runs to full completion — nothing times out and all
 * the data lands. A failure in one pass is caught and the sequence continues (fail-soft).
 *
 * The pure "should this pass run?" logic lives in @krit/core (`autopop.ts`) so the server and the
 * client agree on it and it can be unit-tested — this file only owns the ORDER and the HTTP calls.
 */

// Re-exported so callers keep importing the gate from one place alongside the steps.
export {
  AUTOPOP_STEP_KEYS, shouldRunStep, shouldAutoRunStep, hasAttempt, needsAutoPopulate,
  unproductiveAttempts, type AutopopDealState,
} from '@krit/core';
export type { AutopopStepKey } from '@krit/core';

/** Loader stages — one per pass, plus a terminal "Done". Indices align with AUTOPOP_STEPS. */
export const AUTOPOP_STAGES = ['Deck', 'Briefing', 'Questions', 'Web research', 'Registry & leadership', 'Team research', 'Industry', 'Peers', 'Score', 'Krit Score', 'Done'];

/**
 * The ordered passes. Each is its own foreground request (idempotent + blanks-only server-side).
 * `fresh` is true only on a FORCE run (the ↻ Auto-populate / ↻ Refresh research buttons): it
 * bypasses the 24h enrichment cache so the refresh gets NEW web data (and spends Firecrawl
 * credits). On an auto first-open run `fresh` is false, so cached passes are served free. Deck
 * extraction has no web cache — it stays blanks-only and ignores `fresh`.
 */
export const AUTOPOP_STEPS: { key: AutopopStepKey; label: string; run: (id: string, fresh: boolean) => Promise<unknown> }[] = [
  { key: 'deck', label: 'Deck', run: (id) => extractFromDeck(id) },
  // Deck-mined briefing (timeline, product table, plans, key numbers, open questions) — free, no web.
  { key: 'briefing', label: 'Briefing', run: (id, fresh) => briefingFetch(id, fresh) },
  // Deck-derived "questions to ask the promoter" — free, no web; excludes deck-answered.
  { key: 'questions', label: 'Questions', run: (id, fresh) => questionsFetch(id, fresh) },
  // Research runs BEFORE registry so the CIN it finds lets registry construct + direct-fetch the
  // Tofler MCA page (works even when the search provider can't surface that URL).
  { key: 'research', label: 'Web research', run: (id, fresh) => researchFetch(id, fresh) },
  { key: 'registry', label: 'Registry & leadership', run: (id, fresh) => registryFetch(id, undefined, fresh) },
  // Per-person team depth runs AFTER registry — it reads deck team ∪ MCA directors ∪ web leadership.
  { key: 'team', label: 'Team research', run: (id, fresh) => teamResearchFetch(id, fresh) },
  { key: 'industry', label: 'Industry', run: (id, fresh) => industryFetch(id, fresh) },
  { key: 'peers', label: 'Peers', run: (id, fresh) => peersPopulate(id, fresh) },
  // LLM overall read runs LAST — it scores over everything the passes above just filled in.
  { key: 'score', label: 'Score', run: (id, fresh) => scoreFetch(id, fresh) },
  // Module ratings (the Krit Score sliders) — same corpus as 'score', so it also runs last. Applies
  // the suggestion straight to deals.ratings when the sliders are still untouched (no analyst has
  // rated this deal by hand yet), so the Krit Score tab is never left showing a flat, meaningless
  // default score next to a real AI read.
  { key: 'moduleRatings', label: 'Krit Score', run: (id, fresh) => moduleRatingsFetch(id, fresh) },
];

/** Human labels for the passes, for messages outside the loader (e.g. the "didn't produce" note). */
export const AUTOPOP_LABELS: Record<AutopopStepKey, string> = Object.fromEntries(
  AUTOPOP_STEPS.map((s) => [s.key, s.label]),
) as Record<AutopopStepKey, string>;
